import { useSuspenseInfiniteQuery } from '@tanstack/react-query'

import type { BusinessSummaryItem } from 'src/features/business/shared/schemas/business-item'
import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'

export type GenericSearchFilter = 'users' | 'businesses'

export interface GenericSearchWithFilterParams {
  filter?: GenericSearchFilter
  query?: string
  limit?: number
  skip?: number
}

export type GenericSearchWithFilterResponse = BusinessSummaryItem[] | PublicUserData[]

const getGenericSearchWithFilter = async ({
  filter,
  query,
  limit = 10,
  skip = 0,
}: GenericSearchWithFilterParams): Promise<GenericSearchWithFilterResponse> => {
  if (!filter) {
    return []
  }

  try {
    const params = new URLSearchParams()
    if (query !== undefined) {
      params.append('query', query)
    }
    params.append('limit', limit.toString())
    params.append('skip', skip.toString())

    const response = await getRequest<GenericSearchWithFilterResponse>(
      `/users/search-v2/${filter}?${params.toString()}`,
    )
    return response ?? []
  } catch (error) {
    console.error('Error fetching generic search with filter:', error)
    return []
  }
}

export const useGenericSearchWithFilter = ({ filter, query, limit = 5, skip = 0 }: GenericSearchWithFilterParams) => {
  return useSuspenseInfiniteQuery({
    queryKey: queries.search.genericWithFilter(filter, query, limit, skip).queryKey,
    queryFn: ({ pageParam }) =>
      getGenericSearchWithFilter({
        filter,
        query,
        limit,
        skip: pageParam as number,
      }),
    initialPageParam: skip,
    getNextPageParam: (lastPage, allPages) => {
      if (!filter) {
        return undefined
      }

      const lastUsersPage = lastPage as unknown[]
      if (lastUsersPage.length < limit) {
        return undefined
      }
      const loadedCount = allPages.reduce((acc, page) => acc + (page as unknown[]).length, 0)
      return skip + loadedCount
    },
  })
}
